/**
 * Hook do Calendário de Caixa do ciclo inter-6DU (Task #49).
 *
 * Mesmo padrão do usePosicaoAberta: cache module-level + refetch periódico
 * (TTL 10min — muda quando a Mesa atualiza trades/vencimentos). Em erro de
 * rede/auth/5xx → estado vazio + flag de erro; a UI decide o empty state.
 *
 * #244: recebe `ancoraIso` (6du navegado) e CHAVEIA o cache por ela — sem
 * isso a tabela congelaria no 1º ciclo carregado ao navegar (classe do bug
 * #206: cache module-level único). Padrão idêntico ao useVarejo/usePosicaoAberta.
 */
import { useEffect, useState } from "react";
import { getCalendarioCaixa } from "../api/client";
import type { CalendarioCaixaResponse } from "../api/types";
import { startVisiblePolling } from "./visiblePolling";

const REFETCH_MS = 10 * 60 * 1000;

const EMPTY: CalendarioCaixaResponse = {
  ciclo_inicio: null,
  ciclo_fim: null,
  dias: [],
  sem_data_net: "0",
  sem_data_num_trades: 0,
  movimento_total: "0",
};

interface CacheEntry {
  expiresAt: number;
  promise: Promise<CalendarioCaixaResponse>;
}

const cache = new Map<string, CacheEntry>();

export function invalidateCalendarioCaixaCache(): void {
  cache.clear();
}

export interface UseCalendarioCaixaState {
  data: CalendarioCaixaResponse;
  isLoading: boolean;
  error: Error | null;
}

export function useCalendarioCaixa(ancoraIso?: string): UseCalendarioCaixaState {
  const [state, setState] = useState<UseCalendarioCaixaState>({
    data: EMPTY,
    isLoading: true,
    error: null,
  });

  // Chave do cache = âncora ("" = ciclo corrente/default). undefined → ainda
  // carregando o calendário no pai: trata como default.
  const key = ancoraIso ?? "";

  useEffect(() => {
    let cancel = false;
    setState((s) => ({ ...s, isLoading: true }));

    function load() {
      const now = Date.now();
      const hit = cache.get(key);
      if (!hit || hit.expiresAt < now) {
        cache.set(key, {
          expiresAt: now + REFETCH_MS,
          promise: getCalendarioCaixa(undefined, ancoraIso),
        });
      }
      cache
        .get(key)!
        .promise.then((data) => {
          if (cancel) return;
          setState({ data, isLoading: false, error: null });
        })
        .catch((err: Error) => {
          if (cancel) return;
          cache.delete(key);
          setState({ data: EMPTY, isLoading: false, error: err });
        });
    }

    load();
    // #75: tick skipa com a aba em background; volta visivel/focus -> 1 load.
    const stop = startVisiblePolling(load, REFETCH_MS);
    return () => {
      cancel = true;
      stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return state;
}
